import { useLocation } from "react-router";
import useSWRInfinite from "swr/infinite";
import { z } from "zod";
import { PaginationSchema } from "@/ui/pagination/Pagination";
import { Network } from "@/utils/network";
import { zodUnsafeValidate } from "@/utils/zod";
import { InstagramSearch } from "../types";
import { useInstagramSearch } from "./useInstagramSearch";
import { useInstagramSearchListId } from "./useInstagramSearchRouter";

const InstagramSearchListResponseSchema = z.object({
  id: z.number(),
  name: z.string(),
  profiles: InstagramSearch.ProfileSchema.array(),
  pagination: PaginationSchema,
});

export type InstagramSearchListResponse = z.infer<
  typeof InstagramSearchListResponseSchema
>;

export const useInstagramSearchList = () => {
  const config = useInstagramSearch((state) => state.config);
  const listId = useInstagramSearchListId();
  const location = useLocation();

  const response = useSWRInfinite(
    (index, data: InstagramSearchListResponse | null) => {
      if (listId === undefined) {
        return null;
      }

      if (
        data !== null &&
        data.pagination.currentPage === data.pagination.totalPages
      ) {
        return null;
      }

      return [config.api.getList, index, listId, location.key];
    },
    async ([api, index, listId]) => {
      const { data } = await Network.get<InstagramSearchListResponse>(api, {
        page: index + 1,
        id: listId,
      });

      return zodUnsafeValidate(InstagramSearchListResponseSchema, data);
    },
    {
      revalidateIfStale: false,
      revalidateOnFocus: false,
      revalidateFirstPage: false,
      shouldRetryOnError: false,
      keepPreviousData: true,
    },
  );

  const lastPage = response.data?.[response.data.length - 1];

  const pagination = lastPage?.pagination;

  const isPaginationAvailable =
    pagination !== undefined && pagination.currentPage < pagination.totalPages;

  return {
    ...response,
    isPaginationAvailable,
  };
};
